import { createWithEqualityFn } from 'zustand/traditional';
import api from '../services/api';
import type { InspectionOrder, InspectionOrderListResponse } from '../types/inspectionOrder';
import {
  normalizeInspectionOrder,
  normalizeInspectionOrders,
  type RawInspectionOrder
} from '../utils/normalizeInspectionOrder';

interface InspectionOrdersState {
  todaysOrders: InspectionOrder[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  hasLoadedOnce: boolean;
  lastFetchedAt: string | null;
  loadTodaysOrders: () => Promise<void>;
  refreshTodaysOrders: () => Promise<void>;
  clearTodaysOrders: () => void;
  updateOrder: (order: InspectionOrder) => void;
}

const mapRawOrders = (orders: unknown[]): InspectionOrder[] =>
  orders
    .map((order) => normalizeInspectionOrder(order as RawInspectionOrder))
    .filter((order): order is InspectionOrder => order !== null);

const resolveOrdersPayload = (
  payload: InspectionOrder[] | InspectionOrderListResponse | undefined | null
): InspectionOrder[] => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return normalizeInspectionOrders(payload);
  }

  const asAny = payload as any;

  if (Array.isArray(asAny.orders)) {
    return mapRawOrders(asAny.orders);
  }

  if (Array.isArray(asAny.items)) {
    return mapRawOrders(asAny.items);
  }

  if (Array.isArray(asAny.data)) {
    return mapRawOrders(asAny.data);
  }

  return [];
};

const formatOrders = (orders: InspectionOrder[]) => {
  const toTimestamp = (value: string) => {
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
  };

  return [...orders].sort((a, b) => toTimestamp(a.scheduledStart) - toTimestamp(b.scheduledStart));
};

export const useInspectionOrdersStore = createWithEqualityFn<InspectionOrdersState>()((set, get) => {
  let activeController: AbortController | null = null;

  const runWithinAct = (updater: () => void) => {
    const actWrapper = (globalThis as any).__INSPECTA_STORE_ACT__;

    if (typeof actWrapper === 'function') {
      actWrapper(updater);
      return;
    }

    updater();
  };

  const safeSet: typeof set = (partial, replace) => {
    runWithinAct(() => {
      set(partial, replace);
    });
  };

  const abortActiveRequest = () => {
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  };

  const isSameDay = (timestamp: string | null) => {
    if (!timestamp) {
      return false;
    }

    const lastDate = new Date(timestamp);

    if (Number.isNaN(lastDate.getTime())) {
      return false;
    }

    const now = new Date();

    return (
      lastDate.getFullYear() === now.getFullYear() &&
      lastDate.getMonth() === now.getMonth() &&
      lastDate.getDate() === now.getDate()
    );
  };

  const fetchTodaysOrders = async ({ showLoader }: { showLoader: boolean }) => {
    abortActiveRequest();

    const controller = new AbortController();
    activeController = controller;
    let wasAborted = false;

    safeSet({
      isLoading: showLoader,
      isRefreshing: !showLoader,
      error: null
    });

    try {
      const response = (await api.get<InspectionOrder[] | InspectionOrderListResponse>(
        'technicians/me/inspection-orders',
        {
          params: { date: 'today' },
          signal: controller.signal
        }
      )) as unknown as InspectionOrder[] | InspectionOrderListResponse;

      const orders = formatOrders(resolveOrdersPayload(response));

      safeSet({
        todaysOrders: orders,
        lastFetchedAt: new Date().toISOString(),
        error: null
      });
    } catch (error) {
      if (controller.signal.aborted) {
        wasAborted = true;
        return;
      }

      console.error('orderList:today', error);

      safeSet({
        error: 'Aufträge konnten nicht geladen werden. Bitte erneut versuchen.'
      });
    } finally {
      const isCurrentController = activeController === controller;

      if (isCurrentController) {
        activeController = null;
      }

      if (isCurrentController || !wasAborted) {
        safeSet({
          isLoading: false,
          isRefreshing: false
        });
      }

      if (!wasAborted) {
        safeSet({
          hasLoadedOnce: true
        });
      }
    }
  };

  return {
    todaysOrders: [],
    isLoading: false,
    isRefreshing: false,
    error: null,
    hasLoadedOnce: false,
    lastFetchedAt: null,
    loadTodaysOrders: async () => {
      const state = get();

      if (state.isLoading || state.isRefreshing) {
        return;
      }

      if (!state.hasLoadedOnce || !isSameDay(state.lastFetchedAt)) {
        await fetchTodaysOrders({ showLoader: true });
      }
    },
    refreshTodaysOrders: async () => {
      const state = get();

      if (state.isLoading || state.isRefreshing) {
        return;
      }

      await fetchTodaysOrders({ showLoader: false });
    },
    clearTodaysOrders: () => {
      abortActiveRequest();

      safeSet({
        todaysOrders: [],
        isLoading: false,
        isRefreshing: false,
        error: null,
        hasLoadedOnce: false,
        lastFetchedAt: null
      });
    },
    updateOrder: (updatedOrder) => {
      if (!updatedOrder) {
        return;
      }
      safeSet((state) => {
        const index = state.todaysOrders.findIndex((order) => order.id === updatedOrder.id);
        if (index === -1) {
          return {};
        }

        const nextOrders = [...state.todaysOrders];
        nextOrders[index] = {
          ...nextOrders[index],
          ...updatedOrder
        };

        return {
          todaysOrders: formatOrders(nextOrders)
        };
      });
    }
  };
});
